import { request } from './client';

/**
 * The organisation chart.
 *
 * Authority belongs to the seat, not to the person in it: somebody gains a region by
 * sitting down and loses it by standing up. The screens here edit seats and who is in
 * them; nothing grants anybody anything directly.
 */

const base = '/api/v1/chart';

// Local, matching the other clients: one line is cheaper to read here than an import.
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

/** How far a seat reaches. The same words the server uses. */
export type Scope = 'Global' | 'Operation' | 'Region' | 'Location';

export interface Reach {
  scope: Scope;
  scopeId: string | null;
  /** The operation, region or store by name. Read-only — the server fills it in. */
  name?: string | null;
}

export interface Profession {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
  /** How many seats are for this trade. What stops a delete. */
  positionCount: number;
}

export interface SaveProfession {
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
}

export interface Holder {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  /** ISO date, no time. Somebody starts on a day, not at an instant. */
  from: string;
  until: string | null;
}

export interface Position {
  id: string;
  code: string;
  title: string;
  departmentId: string;
  departmentName: string;
  professionId: string;
  professionName: string;
  reportsToPositionId: string | null;
  reportsToTitle: string | null;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
  reach: Reach[];
  /** Null is a vacancy, which is a fact the chart exists to show and not a fault. */
  holder: Holder | null;
  /** Everybody who has ever sat here, newest first. Only on the single-seat read. */
  history: Holder[];
}

export interface SavePosition {
  code: string;
  title: string;
  departmentId: string;
  professionId: string;
  reportsToPositionId: string | null;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
  reach: Reach[];
}

export interface SeatableUser {
  id: string;
  fullName: string;
  email: string;
  /** How many seats they already hold. Several is allowed and worth seeing. */
  seats: number;
}

export interface Headcount {
  departmentId: string;
  departmentName: string;
  seats: number;
  filled: number;
  vacant: number;
}

export interface GroupMember {
  id: string;
  userId: string;
  fullName: string;
  email: string;
}

/**
 * A named set of people, cutting across the chart.
 *
 * Grants roles and not reach. Reach belongs to the seat, so that "why does this person see
 * this store" keeps one answer.
 */
export interface UserGroup {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
  members: GroupMember[];
}

export interface SaveUserGroup {
  code: string;
  name: string;
  description: string | null;
  isActive: boolean;
  roleIds: string[];
  memberIds: string[];
}

export const chart = {
  groups: {
    list: () => request<UserGroup[]>(`${base}/groups`),
    create: (body: SaveUserGroup) => request<UserGroup>(`${base}/groups`, json('POST', body)),
    update: (id: string, body: SaveUserGroup) =>
      request<UserGroup>(`${base}/groups/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${base}/groups/${id}`, { method: 'DELETE' }),
  },

  professions: {
    list: () => request<Profession[]>(`${base}/professions`),
    create: (body: SaveProfession) =>
      request<Profession>(`${base}/professions`, json('POST', body)),
    update: (id: string, body: SaveProfession) =>
      request<Profession>(`${base}/professions/${id}`, json('PUT', body)),
    remove: (id: string) =>
      request<void>(`${base}/professions/${id}`, { method: 'DELETE' }),
  },

  positions: {
    list: () => request<Position[]>(`${base}/positions`),
    get: (id: string) => request<Position>(`${base}/positions/${id}`),
    create: (body: SavePosition) => request<Position>(`${base}/positions`, json('POST', body)),
    update: (id: string, body: SavePosition) =>
      request<Position>(`${base}/positions/${id}`, json('PUT', body)),
    remove: (id: string) => request<void>(`${base}/positions/${id}`, { method: 'DELETE' }),

    /**
     * Hands the seat from whoever is in it to somebody else, in one step.
     *
     * The predecessor last day is the day before the successor first day, so there is no
     * overlap and no day the seat is held by nobody. Done as two calls instead, the seat
     * stands empty in between — on screen briefly, and in the history for good if the
     * second call is forgotten.
     */
    handover: (id: string, userId: string, from?: string) =>
      request<Holder>(`${base}/positions/${id}/handover`, json('POST', { userId, from })),

    /** Sits somebody down. Refused while the seat is taken: the chart counts seats, not chairs. */
    seat: (id: string, userId: string, from?: string) =>
      request<Holder>(`${base}/positions/${id}/holders`, json('POST', { userId, from })),
  },

  /** Ends a spell — somebody moving on, or leaving. Their authority stops with it. */
  vacate: (holderId: string, until?: string) =>
    request<Holder>(`${base}/holders/${holderId}/end`, json('POST', { until })),

  seatable: () => request<SeatableUser[]>(`${base}/seatable`),
  headcount: () => request<Headcount[]>(`${base}/headcount`),
};
